/**
 * 設定 → 振込口座 → 口座のシート（#155、docs/64 §3、Figma「振込口座（10/9）」⑬⑭⑮⑯。043・docs/32・#79 の「口座を追加／直す」の画面をシートにした）。管理者だけ
 *   ・一覧の画面（SettingsBankAccounts.tsx）が /settings/bank-accounts/new・/:id で出す。スマホ版＝下から、PC版＝真ん中の枠（520px）
 *   ・頭「取消｜口座を追加（口座を修正）｜保存」。保存は直したときだけ押せる（追加は欄が1つでも入ったら）。
 *     保存していない直しのまま取消・Esc・後ろを押したら「入れた内容は消えます」の確かめ（useBackNav と同じ LeaveConfirm）
 *   ・節：銀行（銀行コード・銀行名。見出しの「？」）／支店（支店コード・支店名）／口座（種別のセグメント・口座番号・口座名義＋注「通帳のカナのとおり」）／
 *     既定の口座（スイッチ）／下に「紙には「…」と印字されます」／修正のときだけ一番下に「口座削除」（朱）
 *   ・入力の行は自社情報と同じ GroupInputRow の wide（名前 128px）。「（必須）」の字は出さない。保存を押して足りない欄があれば、
 *     その行の下に朱の1行（BANK_MSG の文）と最初の欄へ focus
 *   ・コードが4桁（支店は3桁）そろったらマスタに聞いて名前を入れる。無ければコードの行の下に朱の1行（名前は手で入れられる）
 *   ・名前を2文字（支店は1文字）打つと、その行のすぐ下・同じまとまりの中に候補の行（GroupSuggestRow。名前と右にコード）。
 *     ↓↑ で選ぶ（地 --sel＋太字）、Enter か押すとコードと名前が入って候補は閉じる。Esc は候補だけ閉じる。コードが入っているときは出さない
 *   ・既定の口座のスイッチ：最初の1つ（口座が0件の会社の追加）・いま既定の口座はオンのまま押せない（外すだけはできない＝docs/32 決めごと4）。
 *     ほかの口座でオンにして保存すると既定に。修正でスイッチだけを変えたときは POST /bank-accounts/:id/default（前の「既定にする」と同じ）
 *   ・削除：既定でほかに口座があるときは押せず、注（BANK_MSG.defaultInUse）。押すと確かめ（取消／削除）
 *   ・保存・削除・既定にしたら onSaved（一覧がシートを閉じてトースト）
 *   ・数字の欄は全角で打っても半角に直す（送るときに contract が整える）
 *   銀行・支店のマスタ（zengin-code）は GET /banks・/banks/:code/branches。画面から外の API は呼ばない（docs/32 決めごと3）
 */
import { BANK_MSG, BANK_TYPE_LABEL, BankAccountType, SETTINGS_MSG, bankAccountText, digitsOnly, EMPTY_BANK_FORM, formOfRow, splitLegacyBank, type BankAccountForm, type BankAccountRow, type BankAccountsPage, type BankSearch, type BranchSearch } from "@genbaryoku/contract";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { api, ApiError, messageOf } from "../api.js";
import { differs, LeaveConfirm } from "../components/BackButton.jsx";
import { GroupDangerRow, GroupInputRow, GroupList, GroupSection, GroupSuggestList, GroupSuggestRow, GroupSwitchRow, GroupValueRow } from "../components/GroupList.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { ErrorText, Help, guardComposingEnter, useFieldIds } from "../components/ui.jsx";

export type BankAccountsSaved = BankAccountsPage & { message: string };
type Cand = { code: string; label: string };
const FIELDS = ["bankCode", "bankName", "branchCode", "branchName", "accountNo", "holder"] as const;
type FieldKey = (typeof FIELDS)[number];
type Errors = Partial<Record<FieldKey, string>>;

export function BankAccountSheet({
  open,
  target,
  accounts,
  legacy,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** 修正する口座（無ければ追加） */
  target: BankAccountRow | undefined;
  /** 今の口座（最初の1つか・既定でほかに口座があるか） */
  accounts: BankAccountRow[];
  /** 以前の振込先（024 の1行）を欄に分けて追加するとき */
  legacy?: string | undefined;
  onClose: () => void;
  onSaved: (p: BankAccountsSaved) => void;
}) {
  const isNew = !target;
  const ids = useFieldIds(FIELDS);
  const bankListId = useId();
  const branchListId = useId();
  const typeId = useId();
  /** 開いたときの中身（追加：空か以前の振込先を分けたもの）。key が変わる（開き直す）まで同じ */
  const [first] = useState<BankAccountForm>(() => (target ? formOfRow(target) : legacy ? splitLegacyBank(legacy) : { ...EMPTY_BANK_FORM }));
  const [form, setForm] = useState<BankAccountForm>(first);
  const [errors, setErrors] = useState<Errors>({});
  const [serverError, setServerError] = useState<string | undefined>();
  const [askDelete, setAskDelete] = useState(false);
  const [askLeave, setAskLeave] = useState(false);
  const baseline = target ? formOfRow(target) : first;
  const dirty = differs(form, baseline);
  const filled = FIELDS.some((k) => form[k].trim() !== "");

  // ---- 欄（コード → 名前・候補）。前の BankAccountFields と同じ動き ----
  const [bankCands, setBankCands] = useState<Cand[] | null>(null);
  const [branchCands, setBranchCands] = useState<Cand[] | null>(null);
  const [bankActive, setBankActive] = useState(-1);
  const [branchActive, setBranchActive] = useState(-1);
  const [bankNote, setBankNote] = useState<string | undefined>();
  const [branchNote, setBranchNote] = useState<string | undefined>();
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef(form);
  latest.current = form;
  /** 直した欄の朱の1行は消す */
  const set = (patch: Partial<BankAccountForm>) => {
    setForm({ ...latest.current, ...patch });
    latest.current = { ...latest.current, ...patch };
    setServerError(undefined);
    setErrors((es) => {
      const keys = Object.keys(patch).filter((k) => k in es);
      if (!keys.length) return es;
      const next = { ...es };
      for (const k of keys) delete next[k as FieldKey];
      return next;
    });
  };
  const later = (f: () => void) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(f, 300);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const showBankCands = (xs: Cand[] | null) => {
    setBankCands(xs && xs.length ? xs : null);
    setBankActive(-1);
  };
  const showBranchCands = (xs: Cand[] | null) => {
    setBranchCands(xs && xs.length ? xs : null);
    setBranchActive(-1);
  };

  /** 銀行コードが4桁そろった → 銀行名を引く */
  const onBankCode = (raw: string) => {
    const code = digitsOnly(raw).slice(0, 4);
    set({ bankCode: code });
    showBankCands(null);
    setBankNote(undefined);
    if (code.length !== 4) return;
    later(async () => {
      try {
        const r = await api<BankSearch>("GET", `/banks?q=${code}`);
        const hit = r.items.find((b) => b.code === code);
        if (hit) set({ bankCode: code, bankName: hit.nameFull });
        else setBankNote(BANK_MSG.bankNotFound);
      } catch {
        /* マスタが引けなくても手で入れられる */
      }
    });
  };
  /** 銀行名を打つ → 候補（コードが入っていないときだけ） */
  const onBankName = (name: string) => {
    set({ bankName: name });
    setBankNote(undefined);
    if (name.trim().length < 2 || latest.current.bankCode.length === 4) {
      showBankCands(null);
      return;
    }
    later(async () => {
      try {
        const r = await api<BankSearch>("GET", `/banks?q=${encodeURIComponent(name.trim())}`);
        showBankCands(r.items.filter((b) => b.nameFull !== name.trim()).map((b) => ({ code: b.code, label: b.nameFull })));
      } catch {
        showBankCands(null);
      }
    });
  };
  const pickBank = (c: Cand) => {
    set({ bankCode: c.code, bankName: c.label, branchCode: "", branchName: "" });
    showBankCands(null);
    setBankNote(undefined);
    document.getElementById(ids.branchCode)?.focus();
  };
  const onBranchCode = (raw: string) => {
    const code = digitsOnly(raw).slice(0, 3);
    set({ branchCode: code });
    showBranchCands(null);
    setBranchNote(undefined);
    if (code.length !== 3 || latest.current.bankCode.length !== 4) return;
    later(async () => {
      try {
        const r = await api<BranchSearch>("GET", `/banks/${latest.current.bankCode}/branches?q=${code}`);
        const hit = r.items.find((b) => b.code === code);
        if (hit) set({ branchCode: code, branchName: hit.nameFull });
        else setBranchNote(BANK_MSG.branchNotFound);
      } catch {
        /* 手で入れられる */
      }
    });
  };
  const onBranchName = (name: string) => {
    set({ branchName: name });
    setBranchNote(undefined);
    if (name.trim().length < 1 || latest.current.bankCode.length !== 4 || latest.current.branchCode.length === 3) {
      showBranchCands(null);
      return;
    }
    later(async () => {
      try {
        const r = await api<BranchSearch>("GET", `/banks/${latest.current.bankCode}/branches?q=${encodeURIComponent(name.trim())}`);
        showBranchCands(r.items.filter((b) => b.nameFull !== name.trim()).map((b) => ({ code: b.code, label: b.nameFull })));
      } catch {
        showBranchCands(null);
      }
    });
  };
  const pickBranch = (c: Cand) => {
    set({ branchCode: c.code, branchName: c.label });
    showBranchCands(null);
    setBranchNote(undefined);
    document.getElementById(ids.accountNo)?.focus();
  };
  /**
   * 名前の欄のキー：↓↑ で候補を選ぶ、Enter で入れる（候補が出ていて選んでいなければ候補を閉じるだけ＝途中で保存しない）、
   * Esc は候補だけ閉じる。変換確定の Enter は送らない
   */
  const candKeys = (list: Cand[] | null, active: number, setActive: (n: number) => void, pick: (c: Cand) => void, closeList: () => void) => (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      guardComposingEnter(e);
      return;
    }
    if (!list) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(active < 0 ? 0 : (active + 1) % list.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(active <= 0 ? list.length - 1 : active - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = active >= 0 ? list[active] : undefined;
      if (c) pick(c);
      else closeList();
    } else if (e.key === "Escape") {
      // シートの Esc（取消）まで届かせない
      e.preventDefault();
      e.stopPropagation();
      closeList();
    }
  };

  // ---- 保存・既定・削除 ----
  const focusFirst = (es: Errors) => {
    const k = FIELDS.find((f) => es[f]);
    if (k) document.getElementById(ids[k])?.focus();
    return !!k;
  };
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.problem.errors) {
      const es = Object.fromEntries(Object.entries(e.problem.errors).map(([k, v]) => [k, String(v)])) as Errors & Record<string, string>;
      setErrors(es);
      if (!focusFirst(es)) setServerError(Object.values(es)[0]);
      return;
    }
    setServerError(messageOf(e));
  };
  const save = useMutation({
    mutationFn: (v: BankAccountForm) => (target ? api<BankAccountsSaved>("PUT", `/bank-accounts/${target.id}`, v) : api<BankAccountsSaved>("POST", "/bank-accounts", v)),
    onSuccess: onSaved,
    onError,
  });
  const makeDefault = useMutation({
    mutationFn: (id: string) => api<BankAccountsSaved>("POST", `/bank-accounts/${id}/default`),
    onSuccess: onSaved,
    onError: (e) => setServerError(messageOf(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<BankAccountsSaved>("DELETE", `/bank-accounts/${id}`),
    onSuccess: (p) => {
      setAskDelete(false);
      onSaved(p);
    },
    onError: (e) => {
      setAskDelete(false);
      setServerError(messageOf(e));
    },
  });
  const pending = save.isPending || makeDefault.isPending || remove.isPending;
  /** 最初の口座と、いま既定の口座は オンのまま（既定は会社に必ず1つ。外すにはほかの口座を既定にする） */
  const forcedOn = target ? target.isDefault : accounts.length === 0;
  const isDefaultOn = forcedOn || form.isDefault;
  /** 既定でほかに口座がある＝消せない */
  const blocked = !!target?.isDefault && accounts.length > 1;
  const canSave = (isNew ? filled : dirty) && !pending;

  const submit = () => {
    if (!canSave) return;
    setServerError(undefined);
    const local: Errors = {};
    if (!form.bankName.trim()) local.bankName = BANK_MSG.bankNameRequired;
    if (!form.branchName.trim()) local.branchName = BANK_MSG.branchNameRequired;
    if (!digitsOnly(form.accountNo)) local.accountNo = BANK_MSG.accountNoRequired;
    if (!form.holder.trim()) local.holder = BANK_MSG.holderRequired;
    if (Object.keys(local).length) {
      setErrors(local);
      focusFirst(local);
      return;
    }
    showBankCands(null);
    showBranchCands(null);
    // 修正でスイッチだけをオンにした＝前の「既定にする」と同じ
    if (target && !target.isDefault && form.isDefault && !differs({ ...form, isDefault: false }, baseline)) {
      makeDefault.mutate(target.id);
      return;
    }
    save.mutate({ ...form, isDefault: isDefaultOn });
  };
  const cancel = () => {
    if (dirty && !pending) setAskLeave(true);
    else onClose();
  };

  const preview = bankAccountText({ bankCode: digitsOnly(form.bankCode) || null, bankName: form.bankName.trim(), branchCode: digitsOnly(form.branchCode) || null, branchName: form.branchName.trim(), accountType: form.accountType, accountNo: digitsOnly(form.accountNo), holder: form.holder.trim() });
  /** 節の注：候補が出ているとき（⑭）／名前だけでコードが無いとき（⑮） */
  const noteOf = (cands: Cand[] | null, name: string, code: string) => (cands ? BANK_MSG.candHint : name.trim() && !code ? BANK_MSG.nameOnlyNote : undefined);
  const err = (k: FieldKey, more?: string) => errors[k] ?? more;
  const inv = (k: FieldKey, more?: string) => (err(k, more) ? { "aria-invalid": true as const, "aria-describedby": `${ids[k]}-err` } : {});

  return (
    <>
      <Sheet
        open={open}
        title={isNew ? BANK_MSG.addTitle : BANK_MSG.editTitle}
        cancelLabel="取消"
        doneLabel={save.isPending || makeDefault.isPending ? SETTINGS_MSG.saving : SETTINGS_MSG.save}
        doneDisabled={!canSave}
        onCancel={cancel}
        onDone={submit}
        className="banksheet"
        doneId="saveBankAccount"
        testId="bank-sheet"
        formTestId="bankForm"
        cancelTestId="bank-sheet-cancel"
      >
        {isNew && legacy ? (
          <p className="banksheet-note" id="legacyNote">
            以前の振込先「{legacy}」を欄に分けてみました。
          </p>
        ) : null}
        <ErrorText>{serverError}</ErrorText>
        <GroupSection
          title={BANK_MSG.sectionBank}
          help={<Help testId="help-bank-code" text="銀行コード（4桁）を入れると自動で入ります。コードが分からなければ、名前を2文字打つと候補が出ます。自動で入った名前は修正できます。" />}
          note={noteOf(bankCands, form.bankName, form.bankCode)}
          data-testid="bank-sec-bank"
        >
          <GroupList plain>
            <GroupInputRow id={ids.bankCode} label={BANK_MSG.bankCode} wide error={err("bankCode", bankNote)}>
              <input id={ids.bankCode} className="giinp num" inputMode="numeric" maxLength={4} autoComplete="off" value={form.bankCode} {...inv("bankCode", bankNote)} onKeyDown={guardComposingEnter} onChange={(e) => onBankCode(e.target.value)} />
            </GroupInputRow>
            <GroupInputRow id={ids.bankName} label={BANK_MSG.bankName} wide error={err("bankName")}>
              <input
                id={ids.bankName}
                className="giinp"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={!!bankCands}
                aria-controls={bankCands ? bankListId : undefined}
                aria-activedescendant={bankCands && bankActive >= 0 ? `${bankListId}-${bankActive}` : undefined}
                autoComplete="off"
                maxLength={60}
                value={form.bankName}
                {...inv("bankName")}
                onKeyDown={candKeys(bankCands, bankActive, setBankActive, pickBank, () => showBankCands(null))}
                onChange={(e) => onBankName(e.target.value)}
              />
            </GroupInputRow>
            {bankCands ? (
              <GroupSuggestList id={bankListId} label={BANK_MSG.bankCands} testId="bank-cands">
                {bankCands.map((c, i) => (
                  <GroupSuggestRow key={c.code} id={`${bankListId}-${i}`} label={c.label} code={c.code} active={i === bankActive} onPick={() => pickBank(c)} />
                ))}
              </GroupSuggestList>
            ) : null}
          </GroupList>
        </GroupSection>

        <GroupSection title={BANK_MSG.sectionBranch} note={noteOf(branchCands, form.branchName, form.branchCode)} data-testid="bank-sec-branch">
          <GroupList plain>
            <GroupInputRow id={ids.branchCode} label={BANK_MSG.branchCode} wide error={err("branchCode", branchNote)}>
              <input id={ids.branchCode} className="giinp num" inputMode="numeric" maxLength={3} autoComplete="off" value={form.branchCode} {...inv("branchCode", branchNote)} onKeyDown={guardComposingEnter} onChange={(e) => onBranchCode(e.target.value)} />
            </GroupInputRow>
            <GroupInputRow id={ids.branchName} label={BANK_MSG.branchName} wide error={err("branchName")}>
              <input
                id={ids.branchName}
                className="giinp"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={!!branchCands}
                aria-controls={branchCands ? branchListId : undefined}
                aria-activedescendant={branchCands && branchActive >= 0 ? `${branchListId}-${branchActive}` : undefined}
                autoComplete="off"
                maxLength={60}
                value={form.branchName}
                {...inv("branchName")}
                onKeyDown={candKeys(branchCands, branchActive, setBranchActive, pickBranch, () => showBranchCands(null))}
                onChange={(e) => onBranchName(e.target.value)}
              />
            </GroupInputRow>
            {branchCands ? (
              <GroupSuggestList id={branchListId} label={BANK_MSG.branchCands} testId="branch-cands">
                {branchCands.map((c, i) => (
                  <GroupSuggestRow key={c.code} id={`${branchListId}-${i}`} label={c.label} code={c.code} active={i === branchActive} onPick={() => pickBranch(c)} />
                ))}
              </GroupSuggestList>
            ) : null}
          </GroupList>
        </GroupSection>

        <GroupSection title={BANK_MSG.sectionAccount} note={BANK_MSG.holderNote} data-testid="bank-sec-account">
          <GroupList plain>
            <GroupValueRow
              title={BANK_MSG.accountType}
              titleId={typeId}
              className="grow-seg is-in"
              value={<Segmented options={BankAccountType.options.map((t) => ({ value: t, label: BANK_TYPE_LABEL[t], testId: `type-${t}` }))} value={form.accountType} onChange={(t) => set({ accountType: t })} labelledBy={typeId} testId="typeSeg" />}
            />
            <GroupInputRow id={ids.accountNo} label={BANK_MSG.accountNo} wide error={err("accountNo")}>
              <input id={ids.accountNo} className="giinp num" inputMode="numeric" autoComplete="off" maxLength={20} value={form.accountNo} {...inv("accountNo")} onKeyDown={guardComposingEnter} onChange={(e) => set({ accountNo: e.target.value })} />
            </GroupInputRow>
            <GroupInputRow id={ids.holder} label={BANK_MSG.holder} wide error={err("holder")}>
              <input id={ids.holder} className="giinp" autoComplete="off" maxLength={100} value={form.holder} {...inv("holder")} onKeyDown={guardComposingEnter} onChange={(e) => set({ holder: e.target.value })} />
            </GroupInputRow>
          </GroupList>
        </GroupSection>

        <GroupSection note={isNew && accounts.length === 0 ? <span id="defText">{BANK_MSG.firstDefault}</span> : undefined} data-testid="bank-sec-default">
          <GroupList plain>
            <GroupSwitchRow label={BANK_MSG.defaultLabel} name={BANK_MSG.defaultLabel} checked={isDefaultOn} disabled={forcedOn} onChange={(v) => set({ isDefault: v })} testId="defaultSwitch" rowTestId="default-row" />
          </GroupList>
        </GroupSection>
        {preview ? (
          <p className="banksheet-preview" id="bankPreview">
            {BANK_MSG.preview.replace("{text}", preview)}
          </p>
        ) : null}

        {target ? (
          <GroupSection note={blocked ? BANK_MSG.defaultInUse : undefined} data-testid="bank-sec-delete">
            <GroupList plain>
              <GroupDangerRow title={BANK_MSG.delete} buttonId="deleteBankAccount" disabled={blocked || pending} onClick={() => setAskDelete(true)} />
            </GroupList>
          </GroupSection>
        ) : null}
      </Sheet>

      {askDelete && target ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setAskDelete(false)}>
          <div className="dlg" role="alertdialog" aria-label={BANK_MSG.deleteAsk} aria-describedby="bank-delete-desc" data-testid="bank-delete-confirm" onKeyDown={(e) => e.key === "Escape" && setAskDelete(false)}>
            <div className="dh">{BANK_MSG.deleteAsk}</div>
            <div className="db">
              <p style={{ margin: 0 }}>
                <b>{`${target.bankName} ${target.branchName}`.trim()}</b>
              </p>
              <p className="hint" id="bank-delete-desc" style={{ margin: 0 }}>
                {BANK_MSG.deleteBody}
              </p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setAskDelete(false)}>
                取消
              </button>
              <button type="button" className="btn" id="deleteConfirm" style={{ borderColor: "var(--shu)", color: "var(--shu)" }} disabled={remove.isPending} onClick={() => remove.mutate(target.id)}>
                削除
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {askLeave ? (
        <LeaveConfirm
          onStay={() => setAskLeave(false)}
          onLeave={() => {
            setAskLeave(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
